import { useMemo } from 'react';
import type { MatchListValue } from '@/components/tree-dnd/tree-dnd-types';
import { MatchListControl } from '@/components/tree-dnd/tree-dnd-conditions/match-list-control';
import type { MatchListOption } from '@/components/tree-dnd/tree-dnd-conditions/mock-options';
import { Button } from '@/components/ui/button';
import { useTenantSentCampaigns } from '@/hooks/use-campaigns';
import { useI18nContext } from '@/i18n/i18n-react';
import { formatShortDateTime } from '@/lib/utils';
import type { Locales } from '@/i18n/i18n-types';

interface CampaignMatchListProps {
  value: MatchListValue;
  onChange: (value: MatchListValue) => void;
  matchModeHelp?: {
    any: string;
    all: string;
  };
  /** When false, hides the any/all control (use with external match-mode UI). */
  allowAll?: boolean;
}

export function useCampaignMatchListOptions(
  selectedIds: string[]
): MatchListOption[] {
  const { LL, locale } = useI18nContext();
  const campaignsQuery = useTenantSentCampaigns();

  return useMemo(() => {
    const live = (campaignsQuery.data ?? []).map(
      (campaign): MatchListOption => ({
        id: campaign.id,
        label: campaign.name,
        description: formatShortDateTime(
          campaign.launchDateTime,
          locale as Locales
        ),
      })
    );
    const liveIds = new Set(live.map((option) => option.id));
    const ghosts = selectedIds
      .filter((id) => !liveIds.has(id))
      .map(
        (id): MatchListOption => ({
          id,
          label: LL.audiences.filters.campaigns.unavailableCampaign(),
          unavailable: true,
        })
      );
    return [...ghosts, ...live];
  }, [LL, campaignsQuery.data, locale, selectedIds]);
}

export function CampaignMatchList({
  value,
  onChange,
  matchModeHelp,
  allowAll = true,
}: CampaignMatchListProps) {
  const { LL } = useI18nContext();
  const campaignsQuery = useTenantSentCampaigns();
  const options = useCampaignMatchListOptions(value.ids);

  const error = campaignsQuery.error;

  return (
    <div className="flex flex-col gap-3">
      <MatchListControl
        variant="expanded"
        value={value}
        onChange={onChange}
        options={options}
        isLoading={campaignsQuery.isLoading}
        matchModeHelp={matchModeHelp}
        allowAll={allowAll}
      />

      {error ? (
        <div className="flex flex-col gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <p>
            {LL.audiences.filters.campaigns.loadError({
              message: error.message,
            })}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-fit"
            onClick={() => void campaignsQuery.refetch()}
          >
            {LL.audiences.filters.campaigns.retry()}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
